import { defineStore } from "pinia"

import { isEbookSection, isTeachingAidsSection } from "~/helpers"
import http from "~/services/http"
import type { FilterValuesType, ProductFilterStateType, SlugTitleType } from "~/types/schema"

const userProductFilterStore = defineStore("productFilterStoreStore", {
  state: () => ({
    routeData: {} as Record<string, {
      products: ProductFilterStateType[],
      filters: {
        categories: SlugTitleType[],
        grades: SlugTitleType[],
        subjects: SlugTitleType[],
        curriculumTypes: SlugTitleType[],
      },
      filterValues: FilterValuesType,
      valuesloading: boolean,
      loading: boolean,
      currentRequestId: string | null,
      currentFilterRequestId: string | null,
    }>,
    currentRoute: null as string | null,
  }),

  getters: {
    products(): ProductFilterStateType[] {
      if (!this.currentRoute || !this.routeData[this.currentRoute]) {
        return []
      }
      return this.routeData[this.currentRoute].products
    },
    filters(): {
      categories: SlugTitleType[];
      grades: SlugTitleType[];
      subjects: SlugTitleType[];
      curriculumTypes: SlugTitleType[];
      } {
      if (!this.currentRoute || !this.routeData[this.currentRoute]) {
        return {
          categories: [],
          grades: [],
          subjects: [],
          curriculumTypes: [],
        }
      }
      return this.routeData[this.currentRoute].filters
    },
    filterValues(): FilterValuesType {
      if (!this.currentRoute || !this.routeData[this.currentRoute]) {
        return {
          curriculum_types: [],
          ebook_categories: [],
          teaching_aids_categories: [],
          grades: [],
          subjects: [],
        }
      }
      return this.routeData[this.currentRoute].filterValues
    },
    loading(): boolean {
      if (!this.currentRoute || !this.routeData[this.currentRoute]) {
        return false
      }
      return this.routeData[this.currentRoute].loading
    },
    valuesloading(): boolean {
      if (!this.currentRoute || !this.routeData[this.currentRoute]) {
        return false
      }
      return this.routeData[this.currentRoute].valuesloading
    },
  },

  actions: {
    initRouteData(routeName: string) {
      if (!this.routeData[routeName]) {
        this.routeData[routeName] = {
          products: [],
          filters: {
            categories: [],
            grades: [],
            subjects: [],
            curriculumTypes: [],
          },
          filterValues: {
            curriculum_types: [],
            ebook_categories: [],
            teaching_aids_categories: [],
            grades: [],
            subjects: [],
          },
          valuesloading: false,
          loading: false,
          currentRequestId: null,
          currentFilterRequestId: null,
        }
      }
      this.currentRoute = routeName
    },

    getCurrentRouteData() {
      if (!this.currentRoute || !this.routeData[this.currentRoute]) {
        return {
          products: [],
          filters: {
            categories: [],
            grades: [],
            subjects: [],
            curriculumTypes: [],
          },
          filterValues: {
            curriculum_types: [],
            ebook_categories: [],
            teaching_aids_categories: [],
            grades: [],
            subjects: [],
          },
          valuesloading: false,
          loading: false,
          currentRequestId: null,
          currentFilterRequestId: null,
        }
      }
      return this.routeData[this.currentRoute]
    },
    getFetchParams(): Record<string, any> {
      const formattedParams: Record<string, any> = {}
      const currentData = this.getCurrentRouteData()

      const requiredCategories: Record<string, string> = {
        "darba-lapas": "drukajamas-darba-lapas",
        video: "video",
        "parbaudes-darbi": "parbaudes-darbi",
        "interaktivie-materiali": "interaktivie-materiali",
      }

      const categorySet = new Set(currentData.filters.categories.map((category) => category.slug))

      if (this.currentRoute && requiredCategories[this.currentRoute]) {
        categorySet.add(requiredCategories[this.currentRoute])
      }

      if (this.currentRoute === "e-gramatas" && categorySet.size === 0) {
        const allEbookCategories = currentData.filterValues.ebook_categories || []
        allEbookCategories.forEach((category) => categorySet.add(category.slug))
      } else if (this.currentRoute === "metodiskie-lidzekli" && categorySet.size === 0) {
        const allTeachingAidsCategories = currentData.filterValues.teaching_aids_categories || []
        allTeachingAidsCategories.forEach((category) => categorySet.add(category.slug))
      }

      formattedParams["categories[]"] = Array.from(categorySet)

      if (currentData.filters.grades.length > 0) {
        formattedParams["grades[]"] = currentData.filters.grades.map((grade) => grade.slug)
      }

      if (currentData.filters.subjects.length > 0) {
        formattedParams["subjects[]"] = currentData.filters.subjects.map((subject) => subject.slug)
      }

      if (currentData.filters.curriculumTypes.length > 0) {
        formattedParams["curriculum_types[]"] = currentData.filters.curriculumTypes.map((type) => type.slug)
      }

      return formattedParams
    },

    fetchProducts({ successCallback, errorCallback }: IErrorAndSuccessCallbacks) {
      if (!this.currentRoute) {
        return
      }

      const params: Record<string, any> = this.getFetchParams()
      const requestId = Date.now().toString() + Math.random().toString(36).substr(2, 9)
      const routeData = this.getCurrentRouteData()
      routeData.currentRequestId = requestId

      routeData.loading = true
      http.get({
        url: "v1/products/filter",
        params,
        successCallback: (context) => {
          if (this.currentRoute && this.routeData[this.currentRoute]?.currentRequestId === requestId) {
            this.routeData[this.currentRoute].products = context.response._data.products
            this.routeData[this.currentRoute].loading = false
            successCallback && successCallback(context)
          }
        },
        errorCallback: (context) => {
          if (this.currentRoute && this.routeData[this.currentRoute]?.currentRequestId === requestId) {
            this.routeData[this.currentRoute].loading = false
            errorCallback && errorCallback(context)
          }
        },
      })
    },

    fetchFilterValues({ successCallback, errorCallback }: IErrorAndSuccessCallbacks) {
      if (!this.currentRoute) {
        return
      }

      const params = this.getFetchParams()
      const requestId = Date.now().toString() + Math.random().toString(36).substr(2, 9)
      const routeData = this.getCurrentRouteData()
      routeData.currentFilterRequestId = requestId

      routeData.valuesloading = true
      http.get({
        url: "v2/filters",
        params,
        successCallback: (context) => {
          if (this.currentRoute && this.routeData[this.currentRoute]?.currentFilterRequestId === requestId) {
            this.routeData[this.currentRoute].valuesloading = false
            this.routeData[this.currentRoute].filterValues = context.response._data.filters
            successCallback && successCallback(context)
          }
        },
        errorCallback: (context) => {
          if (this.currentRoute && this.routeData[this.currentRoute]?.currentFilterRequestId === requestId) {
            this.routeData[this.currentRoute].valuesloading = false
            errorCallback && errorCallback(context)
          }
        },
      })
    },

    removeFilter(slug: string) {
      if (!this.currentRoute) {
        return
      }

      const currentData = this.getCurrentRouteData()
      const filterArrays = Object.values(currentData.filters)

      for (let i = 0; i < filterArrays.length; i++) {
        const searchArray = filterArrays[i]
        for (let u = 0; u < searchArray.length; u++) {
          if (searchArray[u].slug === slug) {
            searchArray.splice(u, 1)
            return
          }
        }
      }
    },

    resetFilters(newCategories?: SlugTitleType[] | null) {
      const route = useRoute()
      const routeName = route.name as string

      this.initRouteData(routeName)

      const currentData = this.getCurrentRouteData()

      if (newCategories) {
        currentData.filters.categories = newCategories
      } else if (isEbookSection(route)) {
        currentData.filters.categories = [...(this.filterValues.ebook_categories || [])]
      } else if (isTeachingAidsSection(route)) {
        currentData.filters.categories = [...(this.filterValues.teaching_aids_categories || [])]
      } else {
        currentData.filters.categories = []
      }

      currentData.filters.subjects = []
      currentData.filters.grades = []
      currentData.filters.curriculumTypes = []
    },
  },
})

export { userProductFilterStore }
